import { useState, useEffect, useRef } from "react";

interface UseTimelineScrollOptions {
    itemCount: number;
    offset?: number;
    threshold?: number;
}

interface UseTimelineScrollReturn {
    currentItemIndex: number;
    scrollProgress: number;
    scrollOffset: number;
    timelineRef: React.RefObject<HTMLDivElement>;
    itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

export const useTimelineScroll = (options: UseTimelineScrollOptions): UseTimelineScrollReturn => {
    const { itemCount, offset = 0, threshold = 0.5 } = options;
    
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const timelineRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        const handleScroll = () => {
            if (!timelineRef.current) return;
            
            const rect = timelineRef.current.getBoundingClientRect();
            const scrollableHeight = timelineRef.current.scrollHeight - window.innerHeight;
            const scrolledDistance = Math.max(0, -rect.top + offset);
            const progress = scrollableHeight > 0 ? scrolledDistance / scrollableHeight : 0;
            
            // Calculate which item should be active based on scroll progress
            const rawItemIndex = progress * itemCount;
            const itemIndex = Math.min(Math.floor(rawItemIndex), itemCount - 1);
            const activeIndex = Math.max(0, itemIndex);
            
            // Calculate smooth progress within current item
            const itemProgress = rawItemIndex - Math.floor(rawItemIndex);
            
            setCurrentItemIndex(activeIndex);
            setScrollProgress(itemProgress);
            setScrollOffset(scrolledDistance);
        };

        const throttledHandleScroll = throttle(handleScroll, 16); // ~60fps
        window.addEventListener('scroll', throttledHandleScroll, { passive: true });
        handleScroll(); // Initial call
        
        return () => window.removeEventListener('scroll', throttledHandleScroll);
    }, [itemCount, offset]);

    return {
        currentItemIndex,
        scrollProgress,
        scrollOffset,
        timelineRef,
        itemRefs
    };
};

// Simple throttle utility
function throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
    let inThrottle: boolean;
    return ((...args: any[]) => {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    }) as T;
}
